/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  AccountManagementProtectedRoute,
  AccountManagementProvider,
  PageSkeleton,
  STATEMENTS_ROUTE_PERMISSION,
  StatementsRouter as BaseStatementsRouter,
  StatementsSkeleton,
} from "@farmlands/account-management";
import { AppError } from "@farmlands/ui-library";
import { FC } from "react";
import { Navigate } from "react-router-dom";

import { PageContainer } from "../../../components/layout/PageContainer";
import { useThemeBackgroundColor } from "../../../hooks/useThemeBackgroundColor";
import { HOME_ROUTE } from "../../../util/routing";
import { useAccountManagementContextValue } from "../hooks/useAccountManagementContextValue";

/**
 * DigitalFarmer mount of `@farmlands/account-management`'s statements feature.
 */
export const StatementsRouter: FC = () => {
  useThemeBackgroundColor((theme) => theme.colors.shade0);

  const { contextValue, isLoading, error, refetch } = useAccountManagementContextValue();

  if (isLoading) {
    return (
      <PageContainer>
        <PageSkeleton />
      </PageContainer>
    );
  }

  if (error) {
    return <AppError contactUrl={contextValue.config.contactUrl} onTryAgainClick={refetch} />;
  }

  if (!contextValue.account.selectedAccount) {
    return <Navigate to={HOME_ROUTE} replace />;
  }

  return (
    <AccountManagementProvider value={contextValue}>
      <AccountManagementProtectedRoute
        {...STATEMENTS_ROUTE_PERMISSION}
        redirectTo={HOME_ROUTE}
        loadingFallback={<StatementsSkeleton />}
      >
        <PageContainer>
          <BaseStatementsRouter />
        </PageContainer>
      </AccountManagementProtectedRoute>
    </AccountManagementProvider>
  );
};

export default StatementsRouter;
